/**
 * TutorialOfferPrompt
 *
 * A one-time, dismissible prompt that offers the guided tour to brand-new visitors.
 * Shows only on the composer route ("/" or "/compose"), never for shared/deep-link
 * entries, never if the tutorial was already completed, and only once ever
 * (localStorage flag). Accepting starts the existing tutorial.
 */
import { useEffect, useState } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';

const OFFERED_KEY = '8bitcomposer_tutorial_offered';

export function TutorialOfferPrompt() {
  const { isActive, isCompleted, startTutorial } = useTutorial();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isCompleted) return;
    const { pathname, search } = window.location;
    const onComposer = pathname === '/' || pathname === '/compose';
    if (!onComposer || pathname.startsWith('/share/')) return;
    if (/template|share|remix/i.test(search)) return; // skip deep-link / remix entries
    try { if (localStorage.getItem(OFFERED_KEY)) return; } catch { /* ignore */ }
    const t = setTimeout(() => setShow(true), 1500); // let the page settle first
    return () => clearTimeout(t);
  }, [isCompleted]);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(OFFERED_KEY, '1'); } catch { /* ignore */ }
  };
  const accept = () => { dismiss(); startTutorial(); };

  if (!show || isActive || isCompleted) return null;

  return (
    <div
      role="dialog"
      aria-label="Take a guided tour"
      style={{
        position: 'fixed', bottom: '1rem', left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, maxWidth: '92vw', display: 'flex', gap: '0.75rem', alignItems: 'center',
        background: 'var(--color-surface)', color: 'var(--color-fg)',
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md, 8px)',
        boxShadow: 'var(--shadow-lg, 0 6px 24px rgba(0,0,0,0.4))', padding: '0.6rem 0.9rem',
        fontSize: '0.85rem',
      }}
    >
      <span>👋 New here? Take a quick 60-second tour.</span>
      <button
        onClick={accept}
        style={{ background: 'var(--color-accent)', color: 'var(--color-bg)', border: 'none', borderRadius: '4px', padding: '0.35rem 0.7rem', cursor: 'pointer', fontWeight: 600 }}
      >
        Start tour
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss tour offer"
        style={{ background: 'transparent', color: 'var(--color-fg-secondary, inherit)', border: 'none', cursor: 'pointer' }}
      >
        Maybe later
      </button>
    </div>
  );
}
